/**
 * Bounded recovery for Next.js chunk / asset version-skew failures.
 *
 * Clears only Wagamour-managed Cache Storage + unregisters SW, then reloads once.
 * Does not touch cookies, localStorage auth, or unrelated caches.
 */

import { isWagamourManagedCacheName } from '@/lib/pwa/swCachePolicy'

export const ASSET_SKEW_RECOVERY_SESSION_KEY = 'wagamour_asset_skew_recovery_v1'

export type AssetSkewRecoveryResult =
  | 'recovered'
  | 'skipped'
  | 'already_attempted'

function matchesSkewMessage(message: string): boolean {
  const m = message.toLowerCase()
  return (
    m.includes('loading chunk') ||
    m.includes('chunkloaderror') ||
    m.includes('failed to fetch dynamically imported module') ||
    m.includes('importing a module script failed') ||
    m.includes('error loading chunk') ||
    (m.includes('/_next/static/') &&
      (m.includes('failed') || m.includes('error') || m.includes('404')))
  )
}

export function isChunkLoadOrAssetSkewError(error: unknown): boolean {
  if (error == null) return false

  if (typeof error === 'string') {
    return matchesSkewMessage(error)
  }

  if (error instanceof Error) {
    if (error.name === 'ChunkLoadError') return true
    if (matchesSkewMessage(error.message)) return true
    if (error.stack && matchesSkewMessage(error.stack)) return true
    return false
  }

  if (typeof error === 'object') {
    const maybe = error as { name?: unknown; message?: unknown }
    if (maybe.name === 'ChunkLoadError') return true
    if (typeof maybe.message === 'string' && matchesSkewMessage(maybe.message)) {
      return true
    }
  }

  return false
}

/** Script-tag load failures for Next assets (ErrorEvent.target). */
export function isNextStaticScriptLoadFailure(event: Event): boolean {
  const target = event.target
  if (!target || typeof (target as HTMLElement).tagName !== 'string') {
    return false
  }
  const el = target as HTMLElement & { src?: string }
  if (el.tagName !== 'SCRIPT' && el.tagName !== 'LINK') return false
  const src =
    typeof el.src === 'string'
      ? el.src
      : typeof (el as HTMLLinkElement).href === 'string'
        ? (el as HTMLLinkElement).href
        : ''
  return src.includes('/_next/static/')
}

export async function clearWagamourManagedCaches(): Promise<string[]> {
  if (typeof caches === 'undefined') return []
  const names = await caches.keys()
  const deleted: string[] = []
  await Promise.all(
    names.map(async (name) => {
      if (!isWagamourManagedCacheName(name)) return
      const ok = await caches.delete(name)
      if (ok) deleted.push(name)
    })
  )
  return deleted
}

export async function unregisterWagamourServiceWorkers(): Promise<number> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return 0
  }
  const registrations = await navigator.serviceWorker.getRegistrations()
  let count = 0
  await Promise.all(
    registrations.map(async (registration) => {
      const ok = await registration.unregister()
      if (ok) count += 1
    })
  )
  return count
}

function hasAlreadyAttemptedRecovery(): boolean {
  try {
    return sessionStorage.getItem(ASSET_SKEW_RECOVERY_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

function markRecoveryAttempted(): void {
  try {
    sessionStorage.setItem(ASSET_SKEW_RECOVERY_SESSION_KEY, '1')
  } catch {
    // private mode — still attempt one reload
  }
}

/**
 * If `error` looks like Next asset skew / chunk load failure, clear Wagamour SW
 * caches, unregister SW, and reload once per browser session.
 */
export async function recoverFromAssetSkewOnce(
  error: unknown,
  opts?: { reload?: () => void }
): Promise<AssetSkewRecoveryResult> {
  if (typeof window === 'undefined') return 'skipped'
  if (!isChunkLoadOrAssetSkewError(error)) return 'skipped'
  if (hasAlreadyAttemptedRecovery()) return 'already_attempted'

  markRecoveryAttempted()

  try {
    await clearWagamourManagedCaches()
  } catch (err) {
    console.warn('[asset-skew] cache clear failed', err)
  }

  try {
    await unregisterWagamourServiceWorkers()
  } catch (err) {
    console.warn('[asset-skew] SW unregister failed', err)
  }

  const reload = opts?.reload ?? (() => window.location.reload())
  reload()
  return 'recovered'
}
